import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAdvisor } from "@/advisor/AdvisorContext";
import { http, getSessionId, errMsg, newId } from "@/advisor/api";
import { PhoneInput } from "@/components/site/PhoneInput";
import { getSource } from "@/lib/refSource";
import { Icon } from "@/advisor/icons";
import { metaBrowserIds, metaLeadFrom } from "@/lib/meta";

const PREFS = ["WhatsApp", "Telefon", "E-mail"];

export default function AdvisorQuote() {
  const { state, update, bySlug } = useAdvisor();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", phone: "", email: "", contact_pref: "WhatsApp", consent: false });
  const [reqId] = useState(newId);
  const [busy, setBusy] = useState(false);
  if (!state.selected.length) return <Navigate to="/advisor/layihe" replace />;

  const valid = f.name.trim().length >= 2 && f.phone.length === 9 && f.consent && (f.contact_pref !== "E-mail" || f.email.includes("@"));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await http.post("/advisor/leads", {
        ref_source: getSource(), request_id: reqId, session_id: getSessionId(),
        name: f.name, phone: `+994${f.phone}`, email: f.email || null, contact_pref: f.contact_pref, consent: f.consent,
        business_text: state.businessText, business_type: state.business?.key || "other",
        goals: state.goals.filter((g) => g !== "all"), tools: state.tools, tiktok_games: state.tiktokGames,
        selected: state.selected, recommended_extra: state.extraRecommended, meta: metaBrowserIds(),
      });
      if (!r.data.duplicate) metaLeadFrom(r.data.meta_event);
      update({ lastLead: { code: r.data.code, business: state.business, selected: state.selected } });
      nav("/advisor/ugur");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-5 sm:px-6 pt-[110px] pb-24" data-testid="quote-page">
      <p className="eyebrow mb-3">Qiymət təklifi</p>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold">Təklifinizi hara göndərək?</h1>
      <div className="flex flex-wrap gap-2 mt-4" data-testid="quote-selected">
        {state.selected.map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-[#171715]/5 px-3 py-1 text-xs font-semibold">
            <Icon name={bySlug[s]?.icon} className="h-3.5 w-3.5 text-[#b58a3f]" /> {bySlug[s]?.name || s}
          </span>
        ))}
      </div>
      <form onSubmit={submit} className="adv-card p-5 sm:p-6 mt-6 space-y-4">
        <label className="block">
          <span className="text-sm font-medium">Adınız</span>
          <input className="game-input mt-1.5" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="lead-form-name" />
        </label>
        <div>
          <span className="text-sm font-medium">Telefon / WhatsApp</span>
          <div className="mt-1.5"><PhoneInput dark value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="lead-form-phone" /></div>
        </div>
        <label className="block">
          <span className="text-sm font-medium">E-mail <span className="text-[#8b867c]">(istəyə görə)</span></span>
          <input type="email" className="game-input mt-1.5" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="lead-form-email" />
        </label>
        <div>
          <span className="text-sm font-medium">Əlaqə üsulu</span>
          <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="lead-form-contact-pref">
            {PREFS.map((p) => (
              <button type="button" key={p} data-active={f.contact_pref === p} onClick={() => setF({ ...f, contact_pref: p })} className="adv-option py-3 text-sm font-medium text-center" data-testid={`lead-pref-${p}`}>{p}</button>
            ))}
          </div>
        </div>
        <label className="flex items-start gap-3 cursor-pointer pt-1">
          <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-1 h-4 w-4 accent-[#D6AD62]" data-testid="lead-form-consent" />
          <span className="text-sm text-[#cfc9bd]">Məlumatlarımın əlaqə məqsədilə istifadə edilməsinə razıyam.</span>
        </label>
        <button type="submit" className="adv-btn w-full text-base" disabled={!valid || busy} data-testid="btn-submit-lead">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sorğunu göndər"}
        </button>
      </form>
    </div>
  );
}
